/**
 * Content-stream primitives, and the text/colour grammars that go with them
 * (VE-24447).
 *
 * Two groups, and it is worth knowing which is which:
 *
 *  - **Writing** — what the worker needs to draw its OWN marks straight into a
 *    page's content stream rather than via annotations: appending an op-string
 *    to `/Contents`, compensating a page's `/Rotate`, encoding PDF literal
 *    strings, and the 2x3 affine matrix arithmetic those need. Worker-only.
 *  - **Grammar** — what may legally go INTO those strings: which characters a
 *    WinAnsi font can draw (`winAnsiByte`, `isWinAnsiEncodable`,
 *    `unencodableChars`, `stripUnencodable`) and what counts as a colour
 *    (`isBatesColor`, `parseHexRgb`). These have UI consumers too — the Bates
 *    modal refuses what the WinAnsi renderer cannot draw, the watermark drawer
 *    applies the same repertoire (the watermark itself is drawn as outlines
 *    now, but no font covers every script — see glyphOutlines.ts), and the
 *    canvas overlay re-validates a colour before it reaches a CSS value.
 *
 * The grammar lives beside the writer on purpose: a second validator elsewhere
 * is how a preview comes to accept what an export rejects, which is the
 * divergence this module and `watermarkTextMetrics` both exist to prevent.
 *
 * Extracted from `burnInWorker.ts` when Bates stamping (VE-24447) became the
 * second consumer of machinery the watermark work (VE-24693) introduced. The
 * text-watermark path had already grown its own inline copy of the `/Contents`
 * append; a third copy is how the two sides of an arithmetic drift apart while
 * every gate stays green, which is the failure mode `watermarkTextMetrics`
 * exists to prevent on the sizing axis. One implementation, three callers.
 *
 * LEAF MODULE — imports nothing but mupdf's TYPES, exactly like
 * `watermarkTextMetrics`. The burn-in worker is loaded by `test/burn-in/*.mjs`
 * through node's type stripping, with no bundler and no DOM; a runtime import
 * reaching redux, the DOM, or any transitive app code would break that harness.
 * Keep it dependency-free.
 *
 * Coordinates: PDF content space has a BOTTOM-LEFT origin. Callers that work in
 * the editor's top-left frame flip Y themselves before calling in.
 */

/**
 * 2x3 affine matrix `[a b c d e f]`, mapping
 * `(x, y) -> (a·x + c·y + e, b·x + d·y + f)` — PDF's `cm` operand order.
 */
export type Mat = [number, number, number, number, number, number];

export const matTranslate = (tx: number, ty: number): Mat => [
  1,
  0,
  0,
  1,
  tx,
  ty,
];
export const matScale = (sx: number, sy: number): Mat => [sx, 0, 0, sy, 0, 0];
export const matRotate = (deg: number): Mat => {
  const r = (deg * Math.PI) / 180;
  return [Math.cos(r), Math.sin(r), -Math.sin(r), Math.cos(r), 0, 0];
};
const matMul = (a: Mat, b: Mat): Mat => [
  a[0] * b[0] + a[2] * b[1],
  a[1] * b[0] + a[3] * b[1],
  a[0] * b[2] + a[2] * b[3],
  a[1] * b[2] + a[3] * b[3],
  a[0] * b[4] + a[2] * b[5] + a[4],
  a[1] * b[4] + a[3] * b[5] + a[5],
];
export const matCompose = (...ms: Mat[]): Mat =>
  ms.reduce((acc, m) => matMul(acc, m));

/**
 * WinAnsi (CP1252) bytes for the characters that are NOT their own code point —
 * the 0x80–0x9F block, where CP1252 puts typography that Latin-1 leaves as
 * control codes. Curly quotes and en/em dashes live here, and they arrive
 * constantly from paste.
 */
const WIN_ANSI_SPECIALS: Readonly<Record<string, number>> = {
  '€': 0x80,
  '‚': 0x82,
  ƒ: 0x83,
  '„': 0x84,
  '…': 0x85,
  '†': 0x86,
  '‡': 0x87,
  ˆ: 0x88,
  '‰': 0x89,
  Š: 0x8a,
  '‹': 0x8b,
  Œ: 0x8c,
  Ž: 0x8e,
  '‘': 0x91,
  '’': 0x92,
  '“': 0x93,
  '”': 0x94,
  '•': 0x95,
  '–': 0x96,
  '—': 0x97,
  '˜': 0x98,
  '™': 0x99,
  š: 0x9a,
  '›': 0x9b,
  œ: 0x9c,
  ž: 0x9e,
  Ÿ: 0x9f,
};

/**
 * The WinAnsi byte for one character, or `null` when WinAnsi cannot represent
 * it at all (CJK, Cyrillic, most of Latin Extended-A, emoji).
 */
export function winAnsiByte(ch: string): number | null {
  const cp = ch.codePointAt(0);
  if (cp === undefined) {
    return null;
  }
  if (cp >= 0x20 && cp <= 0x7e) {
    return cp;
  }
  if (cp >= 0xa0 && cp <= 0xff) {
    return cp;
  }
  return WIN_ANSI_SPECIALS[ch] ?? null;
}

/** Every character of `s` has a WinAnsi byte. Empty strings are encodable. */
export function isWinAnsiEncodable(s: string): boolean {
  return [...s].every((ch) => winAnsiByte(ch) !== null);
}

/** Drop every character WinAnsi cannot draw. Empty result becomes undefined. */
export function stripUnencodable(s: string | undefined): string | undefined {
  if (!s) {
    return undefined;
  }
  const kept = [...s].filter((ch) => winAnsiByte(ch) !== null).join('');
  return kept.length > 0 ? kept : undefined;
}

/**
 * A colour string safe to hand to BOTH the PDF writer and a CSS value.
 *
 * Lives here, beside `parseHexRgb`, because this module owns the colour grammar.
 * A second validator in another leaf is how a preview comes to accept what an
 * export rejects — the preview/export divergence this whole module exists to
 * prevent, on a third axis.
 *
 * The `#` is REQUIRED, deliberately. `parseHexRgb` accepts a bare `ffffff`, but
 * CSS does not: emotion emits `color:ffffff;`, the browser drops the
 * declaration and the preview inherits its colour, while the export happily
 * draws [1,1,1] — WHITE ON WHITE, an invisible identifier on a released record.
 * Requiring the `#` makes the two agree on what a colour is.
 */
export const isBatesColor = (c: unknown): c is string =>
  typeof c === 'string' && /^#[0-9a-f]{6}$/i.test(c);

/** The characters of `s` that WinAnsi cannot represent, de-duplicated. */
export function unencodableChars(s: string): string[] {
  return [...new Set([...s].filter((ch) => winAnsiByte(ch) === null))];
}

/**
 * The same, for MULTI-LINE text, where a line break is a separator rather than
 * something anyone is asking to print.
 *
 * A newline has no WinAnsi byte, so the plain check above reports one — and a
 * caller that refuses text on that basis refuses every multi-line value it is
 * given. The watermark drawer did exactly that: it joins its lines with "\n",
 * gated Save on the result, and so could not save a two-line watermark at all.
 * The message it printed was accurate and looked empty, because a newline has
 * no glyph to show.
 *
 * This is not waiving the check, it is asking the right question. Nothing draws
 * a line break: `pdfLiteralWinAnsi` turns runs of them into spaces, and the
 * burn-in splits on them through `watermarkTextLines`. They are structure, and
 * the characters on either side are still checked exactly as before.
 */
export function unencodableTextChars(s: string): string[] {
  return unencodableChars(s.replace(/[\r\n]+/g, ''));
}

/**
 * Encode a string as the body of a PDF literal `( ... )` drawn with a WinAnsi
 * simple font — escaping syntax AND encoding bytes.
 *
 * Escaping a JS string and letting mupdf transcode it is NOT enough, and the
 * difference is invisible until it reaches a reader: the string goes out as
 * UTF-8 and a WinAnsi font renders each byte separately, so "Montréal" burns in
 * as "MontrÃ©al" — permanent, unhideable content on an already-released record.
 * Verified against real mupdf: the same string emitted as `Montr\351al` renders
 * correctly. Emitting the bytes here also removes the transcoding step that an
 * unpaired surrogate could use to break out of the literal entirely: mupdf's
 * UTF-16 to UTF-8 encoder, on a lone surrogate, consumes the NEXT code unit, so
 * one placed before an escaped paren swallowed the backslash and the rest of
 * the string executed as content-stream operators. The ops string this
 * produces is pure ASCII, so there is no transcoding left to sit inside.
 *
 * Characters WinAnsi cannot represent are DROPPED here rather than mangled.
 * That is the safe half of a decision whose other half belongs upstream: the
 * modal refuses them with a message, and the asset reader strips them, so by
 * the time a string reaches this function there should be nothing to drop.
 * This is the backstop, not the policy.
 */
export function pdfLiteralWinAnsi(s: string): string {
  let out = '';
  for (const ch of s.replace(/[\r\n]+/g, ' ')) {
    const b = winAnsiByte(ch);
    if (b === null) {
      continue;
    }
    if (ch === '\\' || ch === '(' || ch === ')') {
      out += `\\${ch}`;
    } else if (b < 0x80) {
      out += ch;
    } else {
      // Octal byte escape — the portable way to put a high WinAnsi byte in a
      // literal without depending on how the JS string is transcoded.
      out += `\\${b.toString(8).padStart(3, '0')}`;
    }
  }
  return out;
}

/**
 * Parse a `#RRGGBB` / `RRGGBB` hex colour to mupdf's 0..1 RGB triple, falling
 * back to `fallback` when the string is absent or unparseable.
 *
 * The fallback is a PARAMETER because the two callers want different ones and
 * the difference is meaningful: a watermark defaults to mid-grey (decoration,
 * and it sits over content it must not hide), a Bates stamp to black (an
 * identifier that has to read as document text).
 */
export function parseHexRgb(
  hex: string | undefined,
  fallback: [number, number, number]
): [number, number, number] {
  const m = /^#?([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(hex ?? '');
  if (!m) {
    return fallback;
  }
  return [
    parseInt(m[1] ?? '80', 16) / 255,
    parseInt(m[2] ?? '80', 16) / 255,
    parseInt(m[3] ?? '80', 16) / 255,
  ];
}

/**
 * Append an op-string to a page's `/Contents`, wrapping the ORIGINAL content in
 * a balancing `q`/`Q` so the appended ops start from the page's base graphics
 * state rather than whatever unbalanced state the original content left behind.
 *
 * The wrap is load-bearing, not defensive: real-world content streams do leave
 * the graphics stack unbalanced, and without the fence an overlay inherits a
 * stray CTM or clip and lands somewhere else entirely.
 */
export function appendPageOverlay(
  doc: InstanceType<typeof import('mupdf').PDFDocument>,
  pageObj: import('mupdf').PDFObject,
  ops: string
): void {
  const wmStream = doc.addStream(ops, null);
  const qStream = doc.addStream('q\n', null);
  const qEndStream = doc.addStream('\nQ\n', null);
  const contents = pageObj.get('Contents');
  const newArr = doc.newArray();
  newArr.push(qStream);
  if (contents.isArray()) {
    contents.forEach((el) => {
      newArr.push(el);
    });
  } else if (!contents.isNull()) {
    newArr.push(contents);
  }
  newArr.push(qEndStream);
  newArr.push(wmStream);
  pageObj.put('Contents', newArr);
}

/**
 * A `cm` that maps DISPLAY space onto the page's own CONTENT space, for a page
 * carrying `/Rotate`. `null` when there is nothing to compensate.
 *
 * Why this is needed at all: `page.getBounds()` reports the page as the reader
 * SEES it — rotation applied, so a `/Rotate 90` Letter page measures 792×612 —
 * but a content stream is written in the page's own unrotated space, and that
 * is what these operators are appended to. Every other geometry this worker
 * writes goes through annotations, which MuPDF places rotation-aware; the
 * raw content-stream writers are the ones that meet the difference.
 *
 * Uncompensated, a mark on a rotated page came out rotated with the content,
 * anchored nowhere near where it was placed, and sized against the wrong edge
 * (~29% too large on Letter). The editor preview is correct — pdf.js
 * `getViewport({ scale: 1 })` also reports displayed dimensions — so the
 * operator positions a mark in a clear margin and the export puts it sideways
 * across the page body. Landscape-rotated scans are routine in this product.
 *
 * Keeping the whole existing calculation in display space and correcting once,
 * here, is deliberate: the alternative is teaching every anchor, size and angle
 * about rotation, which is four more places to disagree.
 */
export function rotationCompensationCm(
  pageObj: InstanceType<typeof import('mupdf').PDFObject>,
  displayX0: number,
  displayY0: number,
  displayW: number,
  displayH: number
): string | null {
  let deg = 0;
  try {
    const raw = pageObj.getInheritable('Rotate');
    const n = raw?.asNumber?.();
    if (typeof n === 'number' && Number.isFinite(n)) {
      // /Rotate is a multiple of 90 by spec, but files lie; snap and wrap.
      deg = (((Math.round(n / 90) * 90) % 360) + 360) % 360;
    }
  } catch {
    deg = 0;
  }
  if (deg === 0) {
    return null;
  }

  // Unrotated content-space size. A quarter turn swaps the axes.
  const w = deg === 180 ? displayW : displayH;
  const h = deg === 180 ? displayH : displayW;

  // Content-space origin, which is NOT always (0,0). Read from the unrotated
  // MediaBox when it can be had; the display origin is the best available
  // stand-in when it cannot.
  let ox = displayX0;
  let oy = displayY0;
  try {
    const mb = pageObj.getInheritable('MediaBox');
    const mx = mb?.get(0)?.asNumber?.();
    const my = mb?.get(1)?.asNumber?.();
    if (Number.isFinite(mx) && Number.isFinite(my)) {
      ox = Math.min(mx, mb.get(2).asNumber());
      oy = Math.min(my, mb.get(3).asNumber());
    }
  } catch {
    // keep the fallback
  }

  // Display (u,v), taken relative to the display origin, maps to content:
  //   90 : (w - v, u)      180: (w - u, h - v)      270: (v, h - u)
  // written as [a b c d e f] with content = (a·u + c·v + e, b·u + d·v + f),
  // then shifted back onto the real content origin.
  const m: Mat =
    deg === 90
      ? [0, 1, -1, 0, w, 0]
      : deg === 180
        ? [-1, 0, 0, -1, w, h]
        : [0, -1, 1, 0, 0, h];

  const full = matCompose(
    matTranslate(ox, oy),
    m,
    matTranslate(-displayX0, -displayY0)
  );
  return `${full.map((v) => v.toFixed(5)).join(' ')} cm`;
}
